/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, lazy, useEffect, useMemo} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage} from '../modules/auth'
import {RootState} from '../../setup'
import {App} from '../App'
import Cookies from 'js-cookie'
import {AUTH_TOKEN_COOKIE_KEY} from '../constants'
import * as auth from '../../app/modules/auth/redux/AuthRedux'

const IdeasoftRedirect = lazy(() => import('../pages/ideasoft-redirect/IdeasoftRedirect'))
const IdeasoftAppInstall = lazy(() => import('../pages/ideasoft-app-install/Ideasoft-app-install'))
const LogoAuthRedirect = lazy(() => import('../pages/logo-redirect/LogoRedirect'))
/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */

const enableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'flex')
  }
}

const disableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'none')
  }
}

const AppRoutes: FC = () => {
  const dispatch = useDispatch()
  const isAuthorized = useSelector<RootState>(({auth}) => auth.user, shallowEqual)
  const authUser: any = useSelector<RootState>(({auth}) => auth.user)
  const isTokenExpired = useMemo(() => {
    if (authUser) {
      if (new Date() >= new Date(authUser?.token)) {
        Cookies.remove(AUTH_TOKEN_COOKIE_KEY)
        dispatch(auth.actions.logout())
        return true
      }
    }
    return false
  }, [dispatch, authUser])

  useEffect(() => {
    if (isAuthorized && !isTokenExpired) {
      enableSplashScreen()
      setTimeout(() => {
        disableSplashScreen()
      }, 1000)
    }
  }, [dispatch, isAuthorized, isTokenExpired])

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path='ideasoft-oauth2' element={<IdeasoftRedirect />} />
          <Route path='ideasoft-app-install' element={<IdeasoftAppInstall />} />
          <Route path='fleksup-auth' element={<IdeasoftAppInstall />} />
          <Route path='logo-auth-redirect' element={<LogoAuthRedirect />} />
          {isAuthorized && !isTokenExpired ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <>
              <Route path='account/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/account' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
